import { faBirthdayCake, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, lighten, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import React, { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ExportTnABreaksToExcel from "./ExportTnABreaksToExcel/ExportTnABreaksToExcel";
import AnomaliesActions from "./SendAnomaliesNotifications/AnomaliesActions";
import { AbsencesContainer, EmployeeNameContainer, MainTableContainer } from "./styles";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import useForceUpdate from "../../../custom-hooks/useForceUpdate/useForceUpdate";
import { EmployeeDTO, EmployeeRef } from "../../../models/employees";
import { TnAAbsenceDTO } from "../../../models/tna";
import { MyTeamBreaksFilter } from "../../../models/tna/my-team-tna-breaks/my-team-tna-breaks-filter";
import { MyTeamTnADayDataWithBreaksDTO } from "../../../models/tna/tna-day-data-with-breaks/my-team-tna-day-data-with-breaks-dto";
import { TnADayDataWithBreaksRef } from "../../../models/tna/tna-day-data-with-breaks/tna-day-data-with-breaks-ref";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { LightTooltip } from "../../../reusable-components/CustomTooltip/CustomTooltip";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";
import Table from "../../../reusable-components/Table/Table";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";
import { MyTeamBreaksApiService } from "../../../services/my-team-tna-breaks/my-team-tna-breaks-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../utils/constants/filter-tables-constants";
import { englishOrdinalSuffixOf } from "../../../utils/number-ordinal-suffix";
import { getDayAndDateAndMonth, getDaysInMonth, intlFormatDateShort } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import AbsenceButton from "../../MyCalendar/AddEditAbsence/AbsenceButton";
import EditAbsenceWrapper from "../../MyCalendar/AddEditAbsence/EditAbsenceWrapper/EditAbsenceWrapper";
import { AbsenceStatusIndicator } from "../../MyCalendar/AddEditAbsence/styles";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";

const MyTeamBreaks = () => {
  const { t } = useTranslation();
  const tRP = "components.my_team_breaks";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const theme = useTheme();

  const { forceUpdate } = useForceUpdate();

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allItems = useRef<MyTeamTnADayDataWithBreaksDTO[]>([]);
  const [state, setState] = useState({ loading: false });

  const {
    legalEntity: { legalEntityOption, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearMonthQuickFilter },
  } = useContext(MyTnAContext);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const allowedToApproveReject = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const navigate = useNavigate();

  const clickNavigate = (employee: EmployeeDTO | EmployeeRef) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  // #region load employee grpup
  const loadEmployeeGroup = React.useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      console.error(error);
    }
  }, [employeeGroupOption?.employeeGroupIds]);
  // #endregion

  // #region load tna breaks data
  const loadTnABreaksData = React.useCallback(
    async (pageSize = 10) => {
      try {
        setState((state) => ({ ...state, loading: true }));
        const response = await new MyTeamBreaksApiService().loadItems(
          {
            legalEntityIds: legalEntityOption?.legalEntityIds,
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            onlyCurrentEmployees: onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            month: month + 1,
            year,
          } as MyTeamBreaksFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize,
          }
        );

        if (!response.error) {
          return response;
        }

        setState((state) => ({ ...state, loading: false }));
      } catch (error) {
        console.error(error);
      }
    },
    [
      legalEntityOption?.legalEntityIds,
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      month,
      year,
    ]
  );
  // #endregion

  // #region columns
  const columns: GridColDef<MyTeamTnADayDataWithBreaksDTO>[] = [
    {
      field: "employee.employeeCode",
      renderHeader: () => (
        <Typography>{`${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    row.employee && clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      renderHeader: () => (
        <Typography>{`${t("common_words.employee")} | ${t("common_words.legal_entity")}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      renderHeader: () => <Typography>{t("common_words.tna_employee_group")}</Typography>,
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
  ];
  // #endregion

  // #region dyamic columns
  const dynamicColumns = (year: number, month: number) => {
    const result: GridColDef<MyTeamTnADayDataWithBreaksDTO>[] = [];
    const daysInMonth = getDaysInMonth(year, month);
    for (let i = 0; i < daysInMonth; i++) {
      result.push({
        field: new Date(year, month, i + 1).toString(),
        renderCell: ({ row }) => {
          const currDay = row?.daysData?.find((day: TnADayDataWithBreaksRef) => day?.tnADate?.day === i + 1);

          const anomalyDescriptions = Array.from(
            new Set(currDay?.anomalies?.map((a) => a.anomalyType?.description))
          ).join(", ");

          const hasAnomalies = currDay?.hasAnomaly || (currDay?.anomalies && currDay.anomalies.length > 0);

          return (
            <>
              <Grid
                style={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {currDay?.absences.length ? (
                  <AbsencesContainer>
                    {currDay?.absences.map((abs: TnAAbsenceDTO, idx: number) => {
                      if (idx > 0) return;
                      return (
                        <AbsenceStatusIndicator
                          isRejected={abs.isRejected}
                          isCancelled={abs.isCancelled}
                          isApproved={abs.isApproved}
                          key={abs.id}
                        >
                          <EditAbsenceWrapper
                            absence={abs}
                            employee={row.employee}
                            handleNotifyParent={forceUpdate}
                            allowedToApproveReject={allowedToApproveReject}
                            viewMode={allowedToViewOnly && !hasAccess}
                            year={year}
                            month={month}
                            forManager
                            allowedOnlyManagersAction
                          >
                            <AbsenceButton
                              absence={abs}
                              forManager
                              wrapContent
                            />
                          </EditAbsenceWrapper>
                        </AbsenceStatusIndicator>
                      );
                    })}

                    {currDay?.absences.length > 1 && (
                      <SimplePopover
                        buttonType="icon"
                        buttonContent={<FontAwesomeIcon icon={faEllipsis} />}
                        flow="column"
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "center",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "center",
                        }}
                      >
                        {currDay?.absences.slice(1).map((abs: TnAAbsenceDTO, idx: number) => {
                          return (
                            <div
                              style={{
                                marginLeft: "3px",
                                marginRight: "3px",
                                marginBottom: "2px",
                                marginTop: "2px",
                              }}
                              key={idx}
                            >
                              <AbsenceStatusIndicator
                                isRejected={abs.isRejected}
                                isCancelled={abs.isCancelled}
                                isApproved={abs.isApproved}
                                key={abs.id}
                              >
                                <EditAbsenceWrapper
                                  absence={abs}
                                  employee={row.employee}
                                  handleNotifyParent={forceUpdate}
                                  allowedToApproveReject={allowedToApproveReject}
                                  viewMode={allowedToViewOnly && !hasAccess}
                                  year={year}
                                  month={month}
                                  forManager
                                  allowedOnlyManagersAction
                                >
                                  <AbsenceButton
                                    absence={abs}
                                    forManager
                                  />
                                </EditAbsenceWrapper>
                              </AbsenceStatusIndicator>
                            </div>
                          );
                        })}
                      </SimplePopover>
                    )}
                  </AbsencesContainer>
                ) : (
                  <></>
                )}
                <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  {currDay &&
                    (() => {
                      // Calculate total non-automatic breaks
                      let totalBreakMinutes = 0;
                      const breakDetails = (currDay?.breaks || []).map((timeBreak, idx) => {
                        let breakDurationMinutes = 0;
                        if (timeBreak.from && timeBreak.to) {
                          const fromDate = new Date(
                            timeBreak.from.year,
                            timeBreak.from.month - 1,
                            timeBreak.from.day,
                            timeBreak.from.hour,
                            timeBreak.from.minute,
                            timeBreak.from.second
                          );
                          const toDate = new Date(
                            timeBreak.to.year,
                            timeBreak.to.month - 1,
                            timeBreak.to.day,
                            timeBreak.to.hour,
                            timeBreak.to.minute,
                            timeBreak.to.second
                          );
                          breakDurationMinutes = (toDate.getTime() - fromDate.getTime()) / (1000 * 60);
                        }
                        totalBreakMinutes += breakDurationMinutes;
                        return {
                          index: idx + 1,
                          duration: breakDurationMinutes,
                          ordinal: englishOrdinalSuffixOf(idx + 1),
                        };
                      });

                      // Calculate total automatic breaks
                      let totalAutomaticBreakMinutes = 0;
                      const automaticBreakDetails = (currDay?.automaticBreaks || []).map((timeBreak, idx) => {
                        const breakDurationMinutes = timeBreak.substractedDuration || 0;
                        totalAutomaticBreakMinutes += breakDurationMinutes;
                        return {
                          index: idx + 1,
                          duration: breakDurationMinutes,
                          ordinal: englishOrdinalSuffixOf(idx + 1),
                        };
                      });

                      const totalBreaksSum = (totalBreakMinutes + totalAutomaticBreakMinutes)
                        .toFixed(2)
                        .replace(/[.,]00$/, "");
                      const presenceHours = currDay?.presence ? currDay.presence.toFixed(2) : "0";

                      if (!hasAnomalies && presenceHours === "0" && totalBreaksSum === "0") return null;

                      return (
                        <>
                          <LightTooltip
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                  backgroundColor: hasAnomalies ? lighten(theme.palette.error.light, 0.7) : "inherit",
                                  padding: "8px",
                                  borderRadius: "4px",
                                }}
                              >
                                {hasAnomalies && (
                                  <Typography
                                    variant="body2"
                                    style={{ fontSize: "0.75rem", fontWeight: "bold", color: theme.palette.error.main }}
                                  >
                                    {t(`${tRP}.anomalies`)}: {anomalyDescriptions || currDay?.anomalyDescription}
                                  </Typography>
                                )}
                                <Typography
                                  variant="body2"
                                  style={{ fontSize: "0.75rem", fontWeight: "bold" }}
                                >
                                  {t(`${tRP}.worked_time`)}: {presenceHours}h
                                </Typography>
                                {breakDetails.length > 0 ? (
                                  <>
                                    <Typography
                                      variant="body2"
                                      style={{ fontSize: "0.75rem", fontWeight: "bold" }}
                                    >
                                      {t(`${tRP}.breaks`)}:
                                    </Typography>
                                    {breakDetails.map((breakDetail) => (
                                      <Typography
                                        key={breakDetail.index}
                                        variant="body2"
                                        style={{ fontSize: "0.75rem", paddingLeft: "8px" }}
                                      >
                                        {breakDetail.index}
                                        <span style={{ fontSize: "8px", verticalAlign: "super" }}>
                                          {breakDetail.ordinal}
                                        </span>{" "}
                                        {t(`${tRP}.break`)}: {breakDetail.duration.toFixed(2).replace(/[.,]00$/, "")}min
                                      </Typography>
                                    ))}
                                  </>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    style={{ fontSize: "0.75rem", fontWeight: "bold" }}
                                  >
                                    {t(`${tRP}.breaks`)}: 0 min
                                  </Typography>
                                )}
                                {automaticBreakDetails.length > 0 && (
                                  <>
                                    <Typography
                                      variant="body2"
                                      style={{ fontSize: "0.75rem", fontWeight: "bold" }}
                                    >
                                      {t(`${tRP}.automatic_breaks`)}:
                                    </Typography>
                                    {automaticBreakDetails.map((breakDetail) => (
                                      <Typography
                                        key={breakDetail.index}
                                        variant="body2"
                                        style={{ fontSize: "0.75rem", paddingLeft: "8px" }}
                                      >
                                        <span style={{ marginRight: "4px", fontWeight: "bold" }}>Ⓐ</span>
                                        {breakDetail.index}
                                        <span style={{ fontSize: "8px", verticalAlign: "super" }}>
                                          {breakDetail.ordinal}
                                        </span>{" "}
                                        {t(`${tRP}.break`)}: {breakDetail.duration.toFixed(2).replace(/[.,]00$/, "")}min
                                      </Typography>
                                    ))}
                                  </>
                                )}
                              </div>
                            }
                            // arrow
                            slotProps={{
                              tooltip: {
                                sx: {
                                  padding: 0,
                                },
                              },
                              popper: {
                                modifiers: [
                                  {
                                    name: "offset",
                                    options: {
                                      offset: [0, -5],
                                    },
                                  },
                                ],
                              },
                            }}
                          >
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                              <Typography
                                variant="body2"
                                style={{
                                  color: theme.palette.text.primary,
                                  fontSize: "0.75rem",
                                  textAlign: "center",
                                }}
                              >
                                {presenceHours}h / {totalBreaksSum}&apos;
                              </Typography>
                            </div>
                          </LightTooltip>
                          {hasAnomalies && (
                            <AnomaliesActions
                              item={currDay}
                              handleNotifyParent={forceUpdate}
                            />
                          )}
                        </>
                      );
                    })()}
                </Grid>
              </Grid>
            </>
          );
        },
        renderHeader: () => <Typography>{getDayAndDateAndMonth(new Date(year, month, i + 1))}</Typography>,
        headerAlign: "center",
        align: "center",
        flex: 1,
        minWidth: 140,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params: GridCellParams<MyTeamTnADayDataWithBreaksDTO>) => {
          const currDay = new Date(params.field);
          const dayOfWeek: number = currDay.getDay();
          const thisDayData = params.row?.daysData?.find(
            (day: TnADayDataWithBreaksRef) => day?.tnADate?.day === currDay.getDate()
          );

          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          const hasAnomalies = thisDayData?.hasAnomaly || (thisDayData?.anomalies && thisDayData.anomalies.length > 0);

          return hasAnomalies
            ? "hasAnomalies"
            : thisDayData?.isPublicHoliday
            ? "isPublicHoliday"
            : isWeekend
            ? "isWeekend"
            : "";
        },
      });
    }

    return result;
  };
  // #endregion

  const quickfilters = useMemo(() => {
    return (
      <QuickfiltersContainer>
        <>
          {!!legalEntityQuickFilter && legalEntityQuickFilter}
          {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
          {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
          {!!yearMonthQuickFilter && yearMonthQuickFilter}
          <ExportTnABreaksToExcel
            loadEmployeeGroup={loadEmployeeGroup}
            loadTnABreaksData={loadTnABreaksData}
            disabled={state.loading}
            allItems={() => allItems.current as MyTeamTnADayDataWithBreaksDTO[]}
          />
        </>
      </QuickfiltersContainer>
    );
  }, [
    state.loading,
    legalEntityQuickFilter,
    employeeGroupQuickFilter,
    onlyCurrentEmployeesQuickFilter,
    loadEmployeeGroup,
    loadEmployeeGroup,
  ]);

  return (
    <>
      {(hasAccess || allowedToViewOnly) && (
        <MainTableContainer>
          <Table
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new MyTeamBreaksApiService()}
            propsFilter={
              {
                legalEntityIds: legalEntityOption?.legalEntityIds,
                employeeGroupIds: employeeGroupOption?.employeeGroupIds,
                onlyCurrentEmployees: onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
                month: month + 1,
                year,
              } as MyTeamBreaksFilter
            }
            filter={FT_MTPAL}
            readOnly
            quickFilters={
              <QuickfiltersContainer>
                <>{quickfilters}</>
              </QuickfiltersContainer>
            }
            dynamicColumns={dynamicColumns(year, month)}
            pinnedColumns={{
              left: ["employee.employeeCode", "name", "employee.tnAGroup"],
            }}
            getRowIdByItemIndex={true}
            getAllItems={(items) => {
              allItems.current = items as MyTeamTnADayDataWithBreaksDTO[];
            }}
          />
        </MainTableContainer>
      )}
    </>
  );
};

export default MyTeamBreaks;
